<template>
  <settings-modal
    class="send-date-correction-claim-modal t-send-date-correction-claim-modal"
  >
    <span
      slot="heading"
      class="font-weight-bold"
    >Response to received claim</span>

    <v-layout row>
      <v-flex>
        <v-radio-group
          v-model="form.status"
          label="Status code"
          hint="Required field"
          persistent-hint
          :error-messages="getValidationErrorMessage('transferChargeType')"
        >
          <v-radio
            v-for="{ value, name } in statusCodeOptions"
            :key="value"
            style="align-items: unset"
            :class="`t-status-${value.toLowerCase()}`"
            :value="value"
            color="primary"
          >
            <div slot="label">
              <div class="font-weight-medium">{{ value }}</div>
              <div>{{ name }}</div>
            </div>
          </v-radio>
        </v-radio-group>
      </v-flex>
    </v-layout>

    <v-layout
      v-if="isProcessedDateFieldVisible"
      row
    >
      <v-flex xs8>
        <date-picker
          v-model="form.processedDate"
          class="t-processed-date-field"
          label="Date of crediting funds to the client's account"
          hint="Required field"
          persistent-hint
          :error-messages="getValidationErrorMessage('processedDate')"
        />
      </v-flex>
    </v-layout>

    <v-layout
      v-if="isModifiedDateFieldVisible"
      row
    >
      <v-flex xs8>
        <date-picker
          v-model="form.modifiedDate"
          class="t-modified-date-field"
          label="Modified settlement date"
          hint="Required field"
          persistent-hint
          :error-messages="getValidationErrorMessage('modifiedDate')"
        />
      </v-flex>
    </v-layout>

    <v-layout
      v-if="isRejectionCodeFieldVisible"
      row
    >
      <v-flex xs8>
        <v-select
          v-model="form.rejectionCode"
          label="Rejection code"
          hint="Required field"
          item-text="value"
          item-value="value"
          persistent-hint
          :items="rejectionCodeOptions"
          :error-messages="getValidationErrorMessage('rejectionCode')"
        >
          <v-list-tile-content
            slot="item"
            slot-scope="data"
          >
            <v-list-tile-title>{{ data.item.value }}</v-list-tile-title>
            <v-list-tile-sub-title>{{ data.item.name }}</v-list-tile-sub-title>
          </v-list-tile-content>
        </v-select>
      </v-flex>
    </v-layout>

    <error-alert :error="error" />

    <template slot="actions">
      <v-btn
        class="t-send-button"
        color="primary"
        medium
        flat
        :disabled="!isFormFilled || loading"
        @click="onSendButtonClick"
      >send</v-btn>

      <v-btn
        class="t-cancel-button"
        color="primary"
        medium
        flat
        :disabled="loading"
        @click="onCloseButtonClick"
      >cancel</v-btn>
    </template>
  </settings-modal>
</template>

<script>
  // Data
  import claimStatusCodes from '@/assets/data/sepa/claim-status-codes'
  import claimRejectionCodes from '@/assets/data/sepa/claim-rejection-codes'

  // Events
  import { EVENT_PAYMENT_UPDATE } from '@/events/payments'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import PaymentApi from '@/services/Api/PaymentApi'

  // Mixins
  import form from '@/mixins/form'
  import modal from '@/mixins/modal'
  import flashMessages from '@/mixins/flashMessages'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import ErrorAlert from '@/components/ui/ErrorAlert'
  import DatePicker from '@/components/ui/DatePicker'

  export default {
    name: 'SendSepaClaimResponseModal',
    components: {
      SettingsModal,
      ErrorAlert,
      DatePicker
    },
    mixins: [form, modal, flashMessages],
    data () {
      return {
        form: {
          status: null,
          processedDate: null,
          modifiedDate: null,
          rejectionCode: null
        },
        sendRespondToClaimApiRequest: new ApiRequest(PaymentApi.sendRespondToSepaClaim)
      }
    },
    computed: {
      isProcessedDateFieldVisible () {
        return this.parsedFormInput.status === 'ACNR'
      },
      isModifiedDateFieldVisible () {
        return ['ACVA', 'MODI'].includes(this.parsedFormInput.status)
      },
      isRejectionCodeFieldVisible () {
        return this.parsedFormInput.status === 'RJNR'
      },
      payment () {
        return this.modal.data.payment
      },
      loading () {
        return this.sendRespondToClaimApiRequest.loading
      },
      error () {
        return this.sendRespondToClaimApiRequest.error
      },
      requiredFields () {
        const requiredFields = ['status']

        if (this.isProcessedDateFieldVisible) {
          requiredFields.push('processedDate')
        }

        if (this.isModifiedDateFieldVisible) {
          requiredFields.push('modifiedDate')
        }

        if (this.isRejectionCodeFieldVisible) {
          requiredFields.push('rejectionCode')
        }

        return requiredFields
      },
      requestData () {
        return Object
          .entries(this.parsedFormInput)
          .reduce((formData, [fieldName, fieldValue]) => {
            formData[fieldName] = fieldValue !== null ? fieldValue : undefined

            return formData
          }, {})
      },
      statusCodeOptions () {
        return Object
          .entries(claimStatusCodes)
          .reduce((statusCodeOptions, [claimName, claims]) => {
            if (this.payment.tags.includes(claimName)) {
              for (const statusCode in claims) {
                statusCodeOptions.push({
                  value: statusCode,
                  name: claims[statusCode]
                })
              }
            }

            return statusCodeOptions
          }, [])
      },
      rejectionCodeOptions () {
        return Object
          .entries(claimRejectionCodes)
          .reduce((statusCodeOptions, [rejectionCode, rejectionText]) => {
            statusCodeOptions.push({
              value: rejectionCode,
              name: rejectionText
            })

            return statusCodeOptions
          }, [])
      }
    },
    watch: {
      isProcessedDateFieldVisible (isProcessedDateFieldVisible) {
        if (!isProcessedDateFieldVisible) {
          this.setFieldValue('processedDate', null)
        }
      },
      isModifiedDateFieldVisible (isModifiedDateFieldVisible) {
        if (!isModifiedDateFieldVisible) {
          this.setFieldValue('modifiedDate', null)
        }
      },
      isRejectionCodeFieldVisible (isRejectionCodeFieldVisible) {
        if (!isRejectionCodeFieldVisible) {
          this.setFieldValue('rejectionCode', null)
        }
      }
    },
    methods: {
      onSendButtonClick () {
        this.sendRespondToClaimApiRequest
          .execute(this.payment.id, this.requestData)
          .then(() => {
            this.$eventHub.$emit(EVENT_PAYMENT_UPDATE)

            this.showSuccessFlashMessage('Claim response has been successfully send.')
          })
          .catch(() => {})
      },
      onCloseButtonClick () {
        this.closeModal()
      }
    }
  }
</script>
